import React, { useState, useEffect, useRef, useMemo } from 'react';
import { message, Input, Modal, Upload } from 'antd';
import { Users, UserPlus, Plus, Save, Trash2, Info, X, ChevronDown, ChevronUp, Search, Download, Upload as UploadIcon } from 'lucide-react';
import { Button, Checkbox, Spinner, Header, ConfirmationModal } from '@crc-evans/ui-components';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { updateDprSkipState } from '../../redux/dpr/action';
import { addRole, fetchRoles } from '../../redux/roles/action';
import {
  buildDprPersonnelRoleOptions,
  getDprPersonnelAllowedRoles,
  mergeCustomRoleOptions,
} from '../../constants/dprPersonnelRoleGroups';
import { isOffshoreProject } from '../../utils/projectConfig';
import {
  buildOffshoreDisplayName,
  buildOffshorePersonnelEntryFields,
  extractDprPersonnelResponse,
  getDefaultOffshoreRosterShift,
  isWelderJobDescription,
  mapOffshorePersonnelEntryToRow,
  OFFSHORE_ONBOARD_OPTIONS,
  getOffshoreShiftSelectOptions,
  parseOnboardValue,
} from '../../utils/dprOffshorePersonnel';
import {
  downloadOffshorePersonnelTemplate,
  parseOffshorePersonnelExcelRows,
  readExcelRowsFromFile,
} from '../../utils/dprOffshorePersonnelExcel';
import DprPipelineSelect from './DprPipelineSelect';
import './PersonnelLog.css';
const { TextArea } = Input;

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

// Single-select for Name – same styling as Downtime Option / Quality Root dropdown
const CustomNameSelect = ({ value, options = [], onChange, placeholder = 'Select person...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const displayLabel = value || '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          overflow: 'hidden'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        >
          {opts.map((entry, index) => {
            const optValue = typeof entry === 'string' ? entry : (entry.name || entry.value || '');
            const optLabel = typeof entry === 'string' ? entry : (entry.name || entry.label || entry.value || '');
            if (!optValue) return null;
            return (
              <div
                key={entry.id || index}
                onClick={() => { onChange(optValue); setIsOpen(false); }}
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: value === optValue ? '#F3F4F6' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === optValue ? '#F3F4F6' : 'transparent'}
              >
                {optLabel}
              </div>
            );
          })}
          {opts.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomRoleSelect = ({ value, options, onChange, onAddRole, placeholder = "Role...", allowAddNewRole = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newRoleInput, setNewRoleInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsAdding(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [isOpen, options.length, isAdding]);

  const filteredOptions = (options || [])
    .filter((opt) =>
      getRoleDisplayName(opt).toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      getRoleDisplayName(a).localeCompare(getRoleDisplayName(b), undefined, { sensitivity: 'base' })
    );

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = newRoleInput.trim();
    if (!trimmed || !onAddRole) return;
    onAddRole(trimmed);
    setNewRoleInput('');
    setIsAdding(false);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#FFFFFF',
          color: value ? '#1F2937' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search Bar */}
          <div style={{ padding: '8px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={14} color="#9CA3AF" />
            <input
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search roles..."
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: '#1F2937'
              }}
            />
          </div>

          {/* List */}
          <div
            ref={listRef}
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '4px 0'
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((role, index) => {
                const roleName = getRoleDisplayName(role);
                return (
                <div
                  key={getRoleOptionKey(role, index)}
                  onClick={() => {
                    onChange(roleName);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    backgroundColor: value === roleName ? '#F3F4F6' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === roleName ? '#F3F4F6' : 'transparent'}
                >
                  {roleName}
                </div>
                );
              })
            ) : (
              <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
                No roles found
              </div>
            )}
            {allowAddNewRole && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsAdding(true);
              }}
              style={{
                padding: '8px 12px',
                fontSize: '14px',
                color: '#3B82F6',
                fontWeight: 600,
                cursor: 'pointer',
                borderTop: '1px solid #F3F4F6'
              }}
            >
              + Add new role
            </div>
            )}
          </div>

          {/* Footer: Add Role Input - Show only when isAdding is true */}
          {isAdding && (
            <div style={{
              padding: '8px',
              borderTop: '1px solid #F3F4F6',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <input
                autoFocus
                value={newRoleInput}
                onChange={(e) => setNewRoleInput(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.key === 'Enter' && handleAdd(e)}
                placeholder="Enter role name"
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '13px',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  outline: 'none',
                  color: '#1F2937',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  onMouseDown={handleAdd}
                  disabled={!newRoleInput.trim()}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    backgroundColor: newRoleInput.trim() ? '#3B82F6' : '#93C5FD',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: newRoleInput.trim() ? 'pointer' : 'default'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAdding(false);
                    setNewRoleInput('');
                  }}
                  style={{
                    padding: '6px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Role names that should show welder ID / station (matches "Welder", "Lead Welder", etc.). */
const isWelderRoleName = (role) => String(role || '').trim().toLowerCase().includes('weld');

const getRoleDisplayName = (role) =>
  String(role?.roleName ?? role?.name ?? '').trim();

const normalizeGlobalRole = (role) => ({
  roleId: role?.id ?? role?.roleId,
  roleName: getRoleDisplayName(role),
});

const sortRolesByName = (roles) =>
  [...(roles || [])].sort((a, b) =>
    getRoleDisplayName(a).localeCompare(getRoleDisplayName(b), undefined, { sensitivity: 'base' })
  );

const isPendingRoleId = (id) =>
  id == null || (typeof id === 'string' && String(id).startsWith('pending-'));

const findRoleByName = (roles, name) => {
  const q = String(name ?? '').trim().toLowerCase();
  if (!q) return null;
  const matches = (roles || []).filter(
    (r) => getRoleDisplayName(r).toLowerCase() === q
  );
  if (!matches.length) return null;
  return (
    matches.find((r) => !isPendingRoleId(r.roleId ?? r.id)) ?? matches[0]
  );
};

const toPersonnelRoleShape = (role, fallbackName) => {
  const normalized = role ? normalizeGlobalRole(role) : null;
  const roleName = normalized?.roleName || String(fallbackName ?? '').trim();
  if (!roleName) return null;
  const rawId = normalized?.roleId ?? null;
  return {
    roleId: isPendingRoleId(rawId) ? null : rawId,
    roleName,
  };
};

const dedupeRolesByName = (roles) => {
  const byName = new Map();
  (roles || []).forEach((role) => {
    const roleName = getRoleDisplayName(role);
    if (!roleName) return;
    const key = roleName.toLowerCase();
    const existing = byName.get(key);
    const id = role.roleId ?? role.id;
    if (!existing) {
      byName.set(key, role);
      return;
    }
    const existingId = existing.roleId ?? existing.id;
    if (isPendingRoleId(existingId) && !isPendingRoleId(id)) {
      byName.set(key, role);
    }
  });
  return sortRolesByName([...byName.values()]);
};

const getRoleOptionKey = (role, index = 0) => {
  const id = role?.roleId ?? role?.id;
  if (id != null && id !== '') return `role-id-${id}`;
  const name = getRoleDisplayName(role);
  return name ? `role-name-${name.toLowerCase()}` : `role-idx-${index}`;
};

const pendingRoleId = (roleName) =>
  `pending-${String(roleName || '').trim().toLowerCase()}`;

/**
 * Map API personnel entry → table row. Station and welder must be on the row (not only originalEntry)
 * so the listing inputs reflect values after fetch, refresh, or add-from-modal.
 */
const mapPersonnelEntryToRow = (entry, index, shift) => {
  const rawStation =
    entry?.allocatedStation ??
    entry?.station ??
    entry?.stationNumber ??
    entry?.allocated_station ??
    null;
  const allocatedStation =
    rawStation !== undefined && rawStation !== null && String(rawStation).trim() !== ''
      ? String(rawStation).trim()
      : '';

  return {
    id: entry.id || Date.now() + index,
    name: entry.name || '',
    role: entry.role?.roleName || '',
    planned: shift === 'day' ? (entry.plannedDayShift || 0) : (entry.plannedNightShift || 0),
    actual: shift === 'day' ? (entry.actualDayShift || 0) : (entry.actualNightShift || 0),
    userId: entry.userId,
    roleId: entry.role?.roleId,
    welderId: entry.welderId ?? null,
    allocatedStation,
    originalEntry: entry
  };
};

/** First non-empty station from candidates (do not use `|| null` — it drops "0"). */
function pickAllocatedStationString(...candidates) {
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const s = String(c).trim();
    if (s !== '') return s;
  }
  return null;
}

/**
 * DPR personnel API: send both allocatedStation and station (some backends only map one).
 * Uses number when the value is a plain integer string.
 */
function stationFieldsForPersonnelApi(row, originalEntry, newPerson) {
  const str = pickAllocatedStationString(
    newPerson?.allocatedStation,
    newPerson?.station,
    row?.allocatedStation,
    originalEntry?.allocatedStation,
    originalEntry?.station,
    originalEntry?.stationNumber,
    originalEntry?.allocated_station
  );
  if (str === null) {
    return { allocatedStation: null, station: null };
  }
  const n = parseInt(str, 10);
  const val = !Number.isNaN(n) && String(n) === str ? n : str;
  return { allocatedStation: val, station: val };
}

const OFFSHORE_EMPTY_FORM = {
  rosterId: '',
  jobDescription: '',
  subJob: '',
  firstName: '',
  lastName: '',
  nationality: '',
  onboard: 'yes',
  rosterShift: '',
  welderId: '',
  allocatedStation: '',
};

const PersonnelLog = ({
  personnelData: initialPersonnelData = {},
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  isOffshore: isOffshoreProp,
  isShiftSkipped = false,
  isLastDprSection
}) => {
  const [isOffshoreFromApi, setIsOffshoreFromApi] = useState(null);
  const isOffshore =
    isOffshoreProp === true ||
    isOffshoreFromApi === true ||
    (isOffshoreProp !== false && isOffshoreFromApi !== false && isOffshoreProject(selectedProject));
  const [personnelRows, setPersonnelRows] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Totals are always calculated from current row values (UI) for immediate updates in header chips
  const totalPlannedHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.planned) || 0), 0);
  const totalActualHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.actual) || 0), 0);
  const dispatch = useDispatch();
  const globalRoles = useSelector((state) => get(state, 'rolesReduceer.roles', []));
  const skipPersonnel = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipPersonnel || false;
  });
  const [allPersonnelEntries, setAllPersonnelEntries] = useState([]); // Store all entries from API for dropdown
  const [availableRoles, setAvailableRoles] = useState([]); // Store available roles from API
  const [newPersons, setNewPersons] = useState([]); // Store newly added persons before saving
  const [newPersonForm, setNewPersonForm] = useState({
    fullName: '',
    role: '',
    welderId: '',
    allocatedStation: '',
    ...OFFSHORE_EMPTY_FORM,
  });
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [excelPreviewRows, setExcelPreviewRows] = useState([]);
  const [isExcelPreviewOpen, setIsExcelPreviewOpen] = useState(false);
  const [addingNewRole, setAddingNewRole] = useState(null); // Track which row is adding a new role (row.id)
  const [newRoleName, setNewRoleName] = useState(''); // Store the new role name being entered
  const [pendingNewRole, setPendingNewRole] = useState(null);

  const mergedAvailableRoles = useMemo(() => {
    const merged = [...availableRoles];
    (globalRoles || []).forEach((gr) => {
      const normalized = normalizeGlobalRole(gr);
      if (!normalized.roleName) return;
      const exists = merged.some(
        (r) =>
          (normalized.roleId != null && r.roleId === normalized.roleId) ||
          getRoleDisplayName(r).toLowerCase() === normalized.roleName.toLowerCase()
      );
      if (!exists) merged.push(normalized);
    });
    return dedupeRolesByName(merged);
  }, [availableRoles, globalRoles]);

  const filteredAvailableRoles = useMemo(() => {
    const allowed = getDprPersonnelAllowedRoles(globalRoles);
    const base = buildDprPersonnelRoleOptions(mergedAvailableRoles, allowed);
    const activeRoleNames = [
      ...personnelRows.map((row) => row.role),
      newPersonForm.role,
    ].filter(Boolean);
    const merged = mergeCustomRoleOptions({
      baseOptions: base,
      allRoles: mergedAvailableRoles,
      activeRoleNames,
      isPendingRole: (role) => isPendingRoleId(role?.roleId ?? role?.id),
      getName: getRoleDisplayName,
    });
    return sortRolesByName(merged);
  }, [
    mergedAvailableRoles,
    globalRoles,
    personnelRows,
    newPersonForm.role,
  ]);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    if (!pendingNewRole) return;
    const match = findRoleByName(mergedAvailableRoles, pendingNewRole.name);
    if (!match) return;

    const { rowId, name } = pendingNewRole;
    const normalized = normalizeGlobalRole(match);
    const roleName = normalized.roleName;

    setAvailableRoles((prev) => {
      const withoutPending = prev.filter(
        (r) =>
          getRoleDisplayName(r).toLowerCase() !== name.toLowerCase() ||
          !isPendingRoleId(r.roleId)
      );
      const entry = { roleId: normalized.roleId, roleName };
      return dedupeRolesByName([...withoutPending, entry]);
    });

    if (rowId === 'modal') {
      setNewPersonForm((prev) => ({ ...prev, role: roleName }));
    } else {
      setPersonnelRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? { ...row, role: roleName, roleId: normalized.roleId }
            : row
        )
      );
    }
    setPendingNewRole(null);
  }, [pendingNewRole, mergedAvailableRoles]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const handleSkipPersonnelChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipPersonnel', false));
    }
  };

  const applyOffshoreImportedRows = (importedRows) => {
    const timestamp = Date.now();
    const rows = importedRows.map((row, index) => {
      const roleObj = toPersonnelRoleShape(
        findRoleByName(mergedAvailableRoles, row.jobDescription),
        row.jobDescription
      );
      const id = `import-${timestamp}-${index}`;
      const stationStr = row.allocatedStation != null ? String(row.allocatedStation).trim() : '';
      return {
        id,
        rosterId: row.rosterId ?? index + 1,
        firstName: row.firstName ?? '',
        lastName: row.lastName ?? '',
        jobDescription: row.jobDescription ?? '',
        subJob: row.subJob ?? '',
        nationality: row.nationality ?? '',
        onboard: row.onboard ?? null,
        rosterShift: row.rosterShift || getDefaultOffshoreRosterShift(shift),
        name: buildOffshoreDisplayName(row.firstName, row.lastName),
        role: roleObj?.roleName ?? '',
        roleId: roleObj?.roleId ?? null,
        welderId: row.welderId ?? null,
        allocatedStation: stationStr,
        planned: 0,
        actual: 0,
        isNew: true,
        originalEntry: {
          rosterId: row.rosterId ?? index + 1,
          firstName: row.firstName ?? '',
          lastName: row.lastName ?? '',
          jobDescription: row.jobDescription ?? '',
          subJob: row.subJob ?? '',
          nationality: row.nationality ?? '',
          onboard: row.onboard ?? null,
          shift: row.rosterShift ?? '',
          name: buildOffshoreDisplayName(row.firstName, row.lastName),
          role: roleObj ? { roleId: roleObj.roleId, roleName: roleObj.roleName } : undefined,
          welderId: row.welderId ?? null,
          allocatedStation: stationStr || null,
          plannedDayShift: 0,
          actualDayShift: 0,
          plannedNightShift: 0,
          actualNightShift: 0,
        },
      };
    });

    setPersonnelRows(rows);
    setNewPersons(
      rows.map((row) => ({
        id: row.id,
        name: row.name,
        role: row.originalEntry?.role ?? {
          roleId: row.roleId,
          roleName: row.role || row.jobDescription,
        },
        welderId: row.welderId,
        allocatedStation: row.allocatedStation || null,
        remarks: '',
        firstName: row.firstName,
        lastName: row.lastName,
        jobDescription: row.jobDescription,
        subJob: row.subJob,
        nationality: row.nationality,
        onboard: row.onboard,
        rosterShift: row.rosterShift,
        rosterId: row.rosterId,
      }))
    );
    setAllPersonnelEntries(rows.map((row) => ({ ...row.originalEntry, name: row.name })));
  };

  const handleConfirmSkipPersonnel = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipPersonnel', true));
    setIsSkipConfirmOpen(false);
  };

  const handleAddRow = () => {
    const newId = Date.now();
    setPersonnelRows([...personnelRows, {
      id: newId,
      name: '',
      role: '',
      planned: 0,
      actual: 0,
      userId: null,
      roleId: null,
      welderId: null,
      allocatedStation: ''
    }]);
  };

  // Fetch personnel data from API
  const fetchPersonnelData = async () => {
    if (!dprLogId) return; // Don't fetch if dprLogId is not available
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);

      const {
        personnelEntries,
        rolesData,
        notes,
        isOffshoreProject: isOffshoreFromResponse,
      } = extractDprPersonnelResponse(response?.data);

      if (typeof isOffshoreFromResponse === 'boolean') {
        setIsOffshoreFromApi(isOffshoreFromResponse);
      }

      const useOffshoreUi =
        isOffshoreProp === true ||
        isOffshoreFromResponse === true ||
        (isOffshoreProp !== false &&
          isOffshoreFromResponse !== false &&
          isOffshoreProject(selectedProject));

      // Store available roles from API
      if (rolesData && Array.isArray(rolesData) && rolesData.length > 0) {
        setAvailableRoles(sortRolesByName(rolesData));
      } else {
        setAvailableRoles([]);
      }

      if (personnelEntries && Array.isArray(personnelEntries) && personnelEntries.length > 0) {
        setAllPersonnelEntries(personnelEntries);

        const mappedRows = useOffshoreUi
          ? personnelEntries.map((entry, index) => mapOffshorePersonnelEntryToRow(entry, index, shift))
          : personnelEntries.map((entry, index) => mapPersonnelEntryToRow(entry, index, shift));

        setPersonnelRows(mappedRows);

        if (notes !== null && notes !== undefined) {
          setRemarks(notes);
        } else if (notes === '') {
          setRemarks('');
        }
      } else if (useOffshoreUi) {
        setAllPersonnelEntries([]);
        setPersonnelRows([]);
        setNewPersons([]);

        if (notes !== null && notes !== undefined) {
          setRemarks(notes);
        }
      } else {
        setAllPersonnelEntries([]);
        setPersonnelRows([]);

        if (notes !== null && notes !== undefined) {
          setRemarks(notes);
        }
      }
    } catch (error) {
      console.error('Error fetching personnel data:', error);
      setAllPersonnelEntries([]);
      if (initialPersonnelData.personnelRows) {
        setPersonnelRows(initialPersonnelData.personnelRows);
      } else {
        setPersonnelRows([]);
      }
      if (initialPersonnelData.remarks) {
        setRemarks(initialPersonnelData.remarks);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch personnel data from API when shift changes
  useEffect(() => {
    // Clear newPersons when shift changes
    setNewPersons([]);
    fetchPersonnelData();
  }, [shift, dprLogId]); // Re-fetch when shift or dprLogId changes

  // Update local state when prop changes (fallback)
  useEffect(() => {
    if (initialPersonnelData.personnelRows && personnelRows.length === 0) {
      setPersonnelRows(initialPersonnelData.personnelRows);
    }
    if (initialPersonnelData.remarks && !remarks) {
      setRemarks(initialPersonnelData.remarks);
    }
  }, [initialPersonnelData]);

  const savePersonnelData = async (override = {}) => {
    if (isShiftSkipped) return false;
    if (!dprLogId) {
      console.error('dprLogId is required to save personnel');
      return false;
    }
    const rows = override.rows || personnelRows;
    const roles = override.roles || mergedAvailableRoles;
    const newPersonsData = override.newPersons || newPersons;
    const notes = override.remarks !== undefined ? override.remarks : remarks;

    if (isOffshore) {
      for (const row of rows) {
        if (!String(row.firstName ?? '').trim() || !String(row.lastName ?? '').trim()) {
          message.warning('First name and last name are required for all personnel.');
          return false;
        }
        if (!String(row.jobDescription ?? '').trim()) {
          message.warning('Job description is required for all personnel.');
          return false;
        }
        if (
          isWelderJobDescription(row.jobDescription) &&
          !String(row.welderId ?? '').trim()
        ) {
          message.warning(`Welder ID is required for "${row.jobDescription}".`);
          return false;
        }
      }
    }

    setIsSaving(true);
    try {
      // Map personnelRows to API format (existing entries with id)
      const existingEntries = rows
        .filter((row) => {
          if (row.isNew) return false;
          const originalId = row.originalEntry?.id;
          return originalId != null || (typeof row.id === 'number' && Number.isFinite(row.id));
        })
        .map((row) => {
          // Get original entry data to preserve fields
          const originalEntry = row.originalEntry || {};

          // Determine the hours value based on shift
          const hoursValue = parseFloat(row.hours) || 0;

          // Find the role object from availableRoles - use roleId if available, otherwise match by roleName
          const roleObj =
            (row.roleId && roles.find((r) => r.roleId === row.roleId)) ||
            toPersonnelRoleShape(findRoleByName(roles, row.role), row.role) ||
            (isOffshore
              ? toPersonnelRoleShape(findRoleByName(roles, row.jobDescription), row.jobDescription)
              : null) ||
            (originalEntry.role
              ? {
                roleId: originalEntry.role.roleId,
                roleName: originalEntry.role.roleName,
              }
              : null);

          // Get planned and actual values from row
          const plannedValue = parseFloat(row.planned) || 0;
          const actualValue = parseFloat(row.actual) || 0;

          const stationPayload = stationFieldsForPersonnelApi(row, originalEntry, null);

          // Build the entry object
          const entry = {
            id: row.id || originalEntry.id,
            name: isOffshore
              ? buildOffshoreDisplayName(row.firstName, row.lastName, row.name || originalEntry.name)
              : (row.name || originalEntry.name || ''),
            category: originalEntry.category || row.jobDescription || row.role || '',
            remarks: originalEntry.remarks || '',
            role: roleObj ? {
              roleId: roleObj.roleId,
              roleName: roleObj.roleName
            } : (originalEntry.role || {}),
            welderId: originalEntry.welderId || row.welderId || null,
            ...stationPayload,
            ...(isOffshore ? buildOffshorePersonnelEntryFields(row, roleObj) : {}),
          };

          // Update planned and actual hours based on shift
          if (shift === 'day') {
            entry.plannedDayShift = plannedValue;
            entry.plannedNightShift = originalEntry.plannedNightShift || 0;
            entry.actualDayShift = actualValue;
            entry.actualNightShift = originalEntry.actualNightShift || 0;
          } else {
            entry.plannedDayShift = originalEntry.plannedDayShift || 0;
            entry.plannedNightShift = plannedValue;
            entry.actualDayShift = originalEntry.actualDayShift || 0;
            entry.actualNightShift = actualValue;
          }

          return entry;
        });

      // Map newPersons to API format (new entries without id)
      // Get planned/actual values from personnelRows for new persons
      const newEntries = newPersonsData.map((newPerson) => {
        const row = rows.find(r => r.id === newPerson.id && r.isNew);
        const plannedValue = row ? (parseFloat(row.planned) || 0) : 0;
        const actualValue = row ? (parseFloat(row.actual) || 0) : 0;

        const stationPayload = stationFieldsForPersonnelApi(row || {}, {}, newPerson);
        const roleObj = newPerson.role
          ? toPersonnelRoleShape(newPerson.role, newPerson.role.roleName)
          : toPersonnelRoleShape(
            findRoleByName(roles, row?.jobDescription || newPerson.jobDescription),
            row?.jobDescription || newPerson.jobDescription
          );

        const entry = {
          name: isOffshore
            ? buildOffshoreDisplayName(
              row?.firstName ?? newPerson.firstName,
              row?.lastName ?? newPerson.lastName,
              newPerson.name
            )
            : newPerson.name,
          remarks: newPerson.remarks || '',
          role: roleObj
            ? { roleId: roleObj.roleId, roleName: roleObj.roleName }
            : {
              roleId: newPerson.role?.roleId,
              roleName: newPerson.role?.roleName,
            },
          welderId: newPerson.welderId != null && String(newPerson.welderId).trim() !== ''
            ? String(newPerson.welderId).trim()
            : null,
          ...stationPayload,
          ...(isOffshore && row
            ? buildOffshorePersonnelEntryFields(row, roleObj)
            : {}),
        };

        // Update planned and actual hours based on shift
        if (shift === 'day') {
          entry.plannedDayShift = plannedValue;
          entry.plannedNightShift = 0;
          entry.actualDayShift = actualValue;
          entry.actualNightShift = 0;
        } else {
          entry.plannedDayShift = 0;
          entry.plannedNightShift = plannedValue;
          entry.actualDayShift = 0;
          entry.actualNightShift = actualValue;
        }

        return entry;
      });

      // Combine existing and new entries
      const personnelEntries = [...existingEntries, ...newEntries];

      // Calculate totals
      const totalPlanned = personnelEntries.reduce((sum, entry) =>
        sum + (shift === 'day' ? (entry.plannedDayShift || 0) : (entry.plannedNightShift || 0)), 0
      );

      const totalActual = personnelEntries.reduce((sum, entry) =>
        sum + (shift === 'day' ? (entry.actualDayShift || 0) : (entry.actualNightShift || 0)), 0
      );

      // Build the request body
      const requestBody = {
        shift: shift,
        dprPersonnel: {
          dprLogId: dprLogId,
          reportNo: `DPR-${dprLogId}-PE-v1`, // Generate report number
          personnelEntries: personnelEntries,
          shiftLength: 12, // Default or calculate based on requirements
          totalPlanned: totalPlanned,
          totalActual: totalActual,
          notes: notes || '' // Comments/remarks from the textarea go into notes field
        }
      };

      console.log('Remarks/Comments to be saved as notes:', notes);

      console.log('Saving personnel data:', requestBody);

      // Call PUT API
      const response = await axiosConfig.put('/dpr-personnel', requestBody);

      console.log('Personnel save response:', response);

      // Clear newPersons after successful save
      setNewPersons([]);

      // Fetch updated data from GET API to refresh UI
      console.log('Fetching updated personnel data after save...');
      try {
        if (!dprLogId) {
          console.error('dprLogId is required to refresh personnel data');
          message.error('Failed to refresh data: dprLogId is missing');
        } else {
        console.log(`Calling GET API: /dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);
        const getResponse = await axiosConfig.get(`/dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`);

        console.log('Refreshed Personnel API Response:', getResponse);

        // Handle different response structures
        let personnelEntries = null;
        let remarksData = null;
        let rolesData = null;
        let totalPlanned = null;
        let totalActual = null;

        if (getResponse.data) {
          // Check if personnelEntries is directly in response.data
          if (getResponse.data.personnelEntries) {
            personnelEntries = getResponse.data.personnelEntries;
            remarksData = getResponse.data.remarks;
            rolesData = getResponse.data.availableRoles;
            totalPlanned = getResponse.data.totalPlanned;
            totalActual = getResponse.data.totalActual;
          }
          // Check if data is nested in response.data.data
          else if (getResponse.data.data && getResponse.data.data.personnelEntries) {
            personnelEntries = getResponse.data.data.personnelEntries;
            remarksData = getResponse.data.data.remarks;
            rolesData = getResponse.data.data.availableRoles;
            totalPlanned = getResponse.data.data.totalPlanned;
            totalActual = getResponse.data.data.totalActual;
          }
          // Check if response.data is directly an array
          else if (Array.isArray(getResponse.data)) {
            personnelEntries = getResponse.data;
          }
          // Check if response.data is an object with array property
          else if (getResponse.data.data && Array.isArray(getResponse.data.data)) {
            personnelEntries = getResponse.data.data;
          }

          // Also check for availableRoles at root level
          if (!rolesData && getResponse.data.availableRoles) {
            rolesData = getResponse.data.availableRoles;
          }

          // Also check for totals at root level
          if (totalPlanned === null && getResponse.data.totalPlanned !== undefined) {
            totalPlanned = getResponse.data.totalPlanned;
          }
          if (totalActual === null && getResponse.data.totalActual !== undefined) {
            totalActual = getResponse.data.totalActual;
          }
        }

        // Update available roles
        // Update available roles
        // Merge roles from API (rolesData) with roles found in personnelEntries
        let finalRoles = [];
        if (rolesData && Array.isArray(rolesData)) {
          finalRoles = [...rolesData];
        }

        // Add any roles from personnel entries that aren't in the available list
        if (personnelEntries && Array.isArray(personnelEntries)) {
          personnelEntries.forEach(entry => {
            if (entry.role && entry.role.roleName) {
              const exists = finalRoles.find(r => r.roleName === entry.role.roleName);
              if (!exists) {
                finalRoles.push({
                  roleId: entry.role.roleId || `temp-${Date.now()}-${Math.random()}`,
                  roleName: entry.role.roleName
                });
              }
            }
          });
        }

        if (finalRoles.length > 0) {
          setAvailableRoles(sortRolesByName(finalRoles));
        }

        // Update total planned and actual hours from API


        // Update personnel rows with fresh data
        if (personnelEntries && Array.isArray(personnelEntries) && personnelEntries.length > 0) {
          // Store all entries for dropdown options
          setAllPersonnelEntries(personnelEntries);

          const mappedRows = isOffshore
            ? personnelEntries.map((entry, index) => mapOffshorePersonnelEntryToRow(entry, index, shift))
            : personnelEntries.map((entry, index) => mapPersonnelEntryToRow(entry, index, shift));

          setPersonnelRows(mappedRows);

          // Update remarks if available
          if (remarksData) {
            setRemarks(remarksData);
          }


        } else {
          console.warn('No personnel entries found in refreshed data');
        }
        }
      } catch (getError) {
        console.error('Error refreshing personnel data after save:', getError);
        console.error('GET API Error details:', getError.response || getError.message);
        message.warning('Data saved successfully, but failed to refresh. Please reload the page.');
        // Don't fail the save operation if refresh fails
      }

      // Show success message
      message.success('Personnel data saved successfully');

      // Call the onSave callback if provided
      if (onSave) {
        onSave({
          personnelRows,
          remarks,
          success: true,
          response: response.data
        });
      }

      return true;
    } catch (error) {
      console.error('Error saving personnel data:', error);
      console.error('Error response:', error.response);

      // Call onSave with error info
      if (onSave) {
        onSave({
          personnelRows,
          remarks,
          success: false,
          error: error.response?.data || error.message
        });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => savePersonnelData();


  const handleDeleteRow = (id) => {
    setRowToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete;

    try {
      const row = personnelRows.find(r => String(r.id) === String(id));

      // Update local state optimistic
      const updatedRows = personnelRows.filter(r => String(r.id) !== String(id));
      const updatedNewPersons = row?.isNew
        ? newPersons.filter(p => String(p.id) !== String(id))
        : newPersons;

      setPersonnelRows(updatedRows);
      if (row?.isNew) {
        setNewPersons(updatedNewPersons);
      }

      setIsDeleteModalOpen(false);
      setRowToDelete(null);

      // Call API if needed
      if (dprLogId) {
        setIsSaving(true);
        try {
          await savePersonnelData({
            rows: updatedRows,
            newPersons: updatedNewPersons
          });
          message.success('Personnel deleted successfully');
        } catch (error) {
          // Revert
          setPersonnelRows(personnelRows);
          message.error('Failed to delete personnel');
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowChange = (id, field, value) => {
    if (isOffshore) {
      handleOffshoreRowChange(id, field, value);
      return;
    }
    if (field === 'name') {
      // When name is selected, auto-populate role from API data
      const selectedEntry = allPersonnelEntries.find(entry => entry.name === value);
      const selStation =
        selectedEntry?.allocatedStation ??
        selectedEntry?.station ??
        selectedEntry?.stationNumber ??
        selectedEntry?.allocated_station;
      const nextStation =
        selStation !== undefined && selStation !== null && String(selStation).trim() !== ''
          ? String(selStation).trim()
          : '';
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? {
            ...row,
            name: value,
            role: selectedEntry?.role?.roleName || row.role,
            userId: selectedEntry?.userId || row.userId,
            roleId: selectedEntry?.role?.roleId || row.roleId,
            welderId: selectedEntry?.welderId ?? row.welderId,
            allocatedStation: isWelderRoleName(selectedEntry?.role?.roleName || row.role)
              ? (nextStation || row.allocatedStation || '')
              : ''
          }
          : row
      ));
    } else if (field === 'role') {
      // When role is selected, also update roleId
      const roleObj = toPersonnelRoleShape(findRoleByName(filteredAvailableRoles, value), value);
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? {
            ...row,
            role: value,
            roleId: roleObj ? roleObj.roleId : row.roleId,
            allocatedStation: isWelderRoleName(value) ? (row.allocatedStation || '') : ''
          }
          : row
      ));
    } else {
      setPersonnelRows(personnelRows.map(row =>
        row.id === id
          ? { ...row, [field]: value }
          : row
      ));
    }
  };

  const handleOffshoreRowChange = (id, field, value) => {
    setPersonnelRows((rows) =>
      rows.map((row) => {
        if (row.id !== id) return row;

        const next = { ...row, [field]: value };

        if (field === 'jobDescription') {
          const roleObj = toPersonnelRoleShape(
            findRoleByName(filteredAvailableRoles, value),
            value
          );
          next.role = roleObj?.roleName ?? '';
          next.roleId = roleObj?.roleId ?? null;
          if (!isWelderJobDescription(value)) {
            next.welderId = null;
            next.allocatedStation = '';
          }
        }

        if (field === 'firstName' || field === 'lastName') {
          next.name = buildOffshoreDisplayName(
            field === 'firstName' ? value : row.firstName,
            field === 'lastName' ? value : row.lastName
          );
        }

        if (field === 'onboardSelect') {
          next.onboard = value === 'yes' ? true : value === 'no' ? false : null;
        }

        return next;
      })
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadOffshorePersonnelTemplate(personnelRows);
    } catch (error) {
      console.error('Template download error:', error);
      message.error('Failed to download template. Please try again.');
    }
  };

  const handleUploadExcel = () => {
    setIsUploadModalVisible(true);
  };

  const handleUploadModalCancel = () => {
    setIsUploadModalVisible(false);
    setUploadFileList([]);
  };

  const handleUploadExcelFile = async () => {
    if (uploadFileList.length === 0) {
      message.error('Please select an Excel file.');
      return;
    }

    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    if (!file) {
      message.error('Invalid file. Please try again.');
      return;
    }

    try {
      const rows = await readExcelRowsFromFile(file);
      const parsed = parseOffshorePersonnelExcelRows(rows);
      setExcelPreviewRows(parsed);
      setIsUploadModalVisible(false);
      setUploadFileList([]);
      setIsExcelPreviewOpen(true);
    } catch (error) {
      console.error('Excel upload error:', error);
      message.error(error?.message || 'Failed to upload Excel file. Please check the format and try again.');
    }
  };

  const handleConfirmExcelImport = () => {
    applyOffshoreImportedRows(excelPreviewRows);
    setIsExcelPreviewOpen(false);
    setExcelPreviewRows([]);
    message.success(`Imported ${excelPreviewRows.length} personnel row(s). Click Save to persist.`);
  };

  const uploadProps = {
    accept: '.xlsx,.xls',
    multiple: false,
    fileList: uploadFileList,
    beforeUpload: (file) => {
      const isExcelType =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel';
      const isExcelExt = /\.(xlsx|xls)$/i.test(file.name || '');
      if (!isExcelType && !isExcelExt) {
        message.error('Please upload a valid Excel file (.xls or .xlsx).');
        return Upload.LIST_IGNORE;
      }
      setUploadFileList([file]);
      return false;
    },
    onRemove: () => {
      setUploadFileList([]);
    },
  };


  // Header chips: onshore only — sum of planned/actual hours from current rows
  const displayTotalPlanned = totalPlannedHours;
  const displayTotalActual = totalActualHours;

  const handleOpenModal = () => {
    setIsModalVisible(true);
    if (isOffshore) {
      const nextRosterId = personnelRows.length
        ? Math.max(...personnelRows.map((r) => Number(r.rosterId) || 0)) + 1
        : 1;
      setNewPersonForm({
        ...OFFSHORE_EMPTY_FORM,
        rosterId: String(nextRosterId),
        rosterShift: getDefaultOffshoreRosterShift(shift),
      });
    } else {
      setNewPersonForm({ fullName: '', role: '', welderId: '', allocatedStation: '' });
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    if (isOffshore) {
      setNewPersonForm({ ...OFFSHORE_EMPTY_FORM });
    } else {
      setNewPersonForm({ fullName: '', role: '', welderId: '', allocatedStation: '' });
    }
    // Reset adding new role state if it was set for modal
    if (addingNewRole === 'modal') {
      setAddingNewRole(null);
      setNewRoleName('');
    }
  };

  const handleAddPerson = () => {
    if (isOffshore) {
      if (!newPersonForm.firstName?.trim() || !newPersonForm.lastName?.trim() || !newPersonForm.jobDescription?.trim()) {
        message.warning('First name, last name, and job description are required.');
        return;
      }
      if (isWelderJobDescription(newPersonForm.jobDescription) && !newPersonForm.welderId?.trim()) {
        message.warning('Welder ID is required for welding roles');
        return;
      }

      const roleObj = toPersonnelRoleShape(
        findRoleByName(filteredAvailableRoles, newPersonForm.jobDescription),
        newPersonForm.jobDescription
      );
      const newId = Date.now();
      const stationStr = newPersonForm.allocatedStation?.trim() || '';
      const onboard = parseOnboardValue(newPersonForm.onboard);
      const rosterShift = newPersonForm.rosterShift || getDefaultOffshoreRosterShift(shift);
      const rosterId = newPersonForm.rosterId?.trim()
        ? newPersonForm.rosterId.trim()
        : String(personnelRows.length + 1);
      const displayName = buildOffshoreDisplayName(newPersonForm.firstName, newPersonForm.lastName);

      const newPersonEntry = {
        id: newId,
        rosterId,
        firstName: newPersonForm.firstName.trim(),
        lastName: newPersonForm.lastName.trim(),
        jobDescription: newPersonForm.jobDescription.trim(),
        subJob: newPersonForm.subJob?.trim() || '',
        nationality: newPersonForm.nationality?.trim() || '',
        onboard,
        rosterShift,
        name: displayName,
        role: roleObj?.roleName ?? '',
        planned: 0,
        actual: 0,
        roleId: roleObj?.roleId ?? null,
        welderId: newPersonForm.welderId?.trim() || null,
        allocatedStation: stationStr,
        originalEntry: {
          rosterId,
          firstName: newPersonForm.firstName.trim(),
          lastName: newPersonForm.lastName.trim(),
          jobDescription: newPersonForm.jobDescription.trim(),
          subJob: newPersonForm.subJob?.trim() || '',
          nationality: newPersonForm.nationality?.trim() || '',
          onboard,
          shift: rosterShift,
          name: displayName,
          role: roleObj ? { roleId: roleObj.roleId, roleName: roleObj.roleName } : undefined,
          welderId: newPersonForm.welderId?.trim() || null,
          allocatedStation: stationStr || null,
          plannedDayShift: 0,
          actualDayShift: 0,
          plannedNightShift: 0,
          actualNightShift: 0,
        },
        isNew: true,
      };

      setPersonnelRows([...personnelRows, newPersonEntry]);
      setNewPersons([...newPersons, {
        id: newId,
        name: displayName,
        role: roleObj ? { roleId: roleObj.roleId, roleName: roleObj.roleName } : {
          roleId: null,
          roleName: newPersonForm.jobDescription.trim(),
        },
        welderId: newPersonForm.welderId?.trim() || null,
        allocatedStation: stationStr || null,
        remarks: '',
        firstName: newPersonForm.firstName.trim(),
        lastName: newPersonForm.lastName.trim(),
        jobDescription: newPersonForm.jobDescription.trim(),
        subJob: newPersonForm.subJob?.trim() || '',
        nationality: newPersonForm.nationality?.trim() || '',
        onboard,
        rosterShift,
        rosterId,
      }]);
      setAllPersonnelEntries([...allPersonnelEntries, newPersonEntry.originalEntry]);
      handleCloseModal();
      return;
    }

    if (!newPersonForm.fullName || !newPersonForm.role) {
      return; // Required fields validation
    }

    if (isWelderRoleName(newPersonForm.role) && !newPersonForm.welderId?.trim()) {
      message.warning('Welder ID is required for welding roles');
      return;
    }

    const roleObj = toPersonnelRoleShape(
      findRoleByName(filteredAvailableRoles, newPersonForm.role),
      newPersonForm.role
    );

    if (!roleObj?.roleName) {
      message.warning('Please select a valid job title from the list.');
      dispatch(fetchRoles());
      return;
    }

    const newId = Date.now();
    const stationStr =
      newPersonForm.allocatedStation != null && String(newPersonForm.allocatedStation).trim() !== ''
        ? String(newPersonForm.allocatedStation).trim()
        : '';
    const newPersonEntry = {
      id: newId,
      name: newPersonForm.fullName,
      role: roleObj.roleName,
      planned: 0,
      actual: 0,
      roleId: roleObj.roleId,
      welderId: newPersonForm.welderId || null,
      allocatedStation: stationStr,
      originalEntry: {
        name: newPersonForm.fullName,
        role: {
          roleId: roleObj.roleId,
          roleName: roleObj.roleName
        },
        welderId: newPersonForm.welderId || null,
        allocatedStation: stationStr || null,
        remarks: '',
        plannedDayShift: 0,
        actualDayShift: 0,
        plannedNightShift: 0,
        actualNightShift: 0
      },
      isNew: true
    };

    // Add to personnelRows for display
    setPersonnelRows([...personnelRows, newPersonEntry]);

    // Also add to newPersons for API payload
    setNewPersons([...newPersons, {
      id: newId,
      name: newPersonForm.fullName,
      role: {
        roleId: roleObj.roleId,
        roleName: roleObj.roleName
      },
      welderId: newPersonForm.welderId || null,
      allocatedStation: stationStr || null,
      remarks: '',
      plannedDayShift: 0,
      actualDayShift: 0,
      plannedNightShift: 0,
      actualNightShift: 0
    }]);

    // Add to allPersonnelEntries so it appears in the Name dropdown
    setAllPersonnelEntries([...allPersonnelEntries, newPersonEntry]);

    handleCloseModal();
  };

  const handleFormChange = (field, value) => {
    if (isOffshore) {
      if (field === 'jobDescription') {
        setNewPersonForm((prev) => ({
          ...prev,
          jobDescription: value,
          ...(isWelderJobDescription(value) ? {} : { welderId: '', allocatedStation: '' }),
        }));
        return;
      }
      setNewPersonForm((prev) => ({ ...prev, [field]: value }));
      return;
    }
    if (field === 'role') {
      setNewPersonForm((prev) => ({
        ...prev,
        role: value,
        ...(isWelderRoleName(value) ? {} : { welderId: '', allocatedStation: '' })
      }));
      return;
    }
    setNewPersonForm((prev) => ({ ...prev, [field]: value }));
  };

  // Handle adding a new role via POST /role — same pattern as DSR technician log
  const handleAddNewRole = (rowId, name) => {
    const roleName = String(name || newRoleName || '').trim();
    if (!roleName) return;

    const existsInDropdown = filteredAvailableRoles.some(
      (r) => getRoleDisplayName(r).toLowerCase() === roleName.toLowerCase()
    );

    if (!existsInDropdown) {
      setAvailableRoles((prev) => {
        if (findRoleByName(prev, roleName)) return prev;
        return sortRolesByName([...prev, { roleId: pendingRoleId(roleName), roleName }]);
      });
      setPendingNewRole({ rowId, name: roleName });
      dispatch(addRole({ name: roleName, personnelRoleType: 'welding' }));
    }

    if (rowId === 'modal') {
      handleFormChange('role', roleName);
    } else {
      handleRowChange(rowId, 'role', roleName);
    }

    setAddingNewRole(null);
    setNewRoleName('');
  };



  // Determine display values for badge
  const mpCount = personnelRows.length;
  // Calculate total hours from rows. The image shows "2.0 MP" and "24 Hrs"
  // Assuming "Hrs" is total actual or planned hours. Let's use planned for now or just the sum.
  const totalHours = personnelRows.reduce((sum, row) => sum + (parseFloat(row.hours) || 12), 0); // Using default 12 for now if missing

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      {/* Main Container */}
      <div className="dpr-personnel-card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#F3E8FF',
              padding: '10px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9333EA'
            }}>
              <Users size={24} />
            </div>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>
                  Personnel & Manpower
                </h3>
                {!isOffshore ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{
                      backgroundColor: '#F1F5F9',
                      color: '#64748B',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      Planned: {displayTotalPlanned.toFixed(1)} Hrs
                    </span>
                    <span style={{
                      backgroundColor: '#F1F5F9',
                      color: '#64748B',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      Actual: {displayTotalActual.toFixed(1)} Hrs
                    </span>
                  </div>
                ) : null}
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>
                {isOffshore
                  ? 'Log offshore crew roster and shift assignments.'
                  : 'Log team members, roles, and shift hours.'}
              </p>
            </div>
          </div>

          <div className="dpr-personnel-toolbar">
            <Checkbox
              label="Skip Personnel"
              checked={skipPersonnel}
              onChange={handleSkipPersonnelChange}
              borderColor="#00C9A7"
            />
            {isOffshore ? (
              <div className="dpr-personnel-toolbar__excel-actions">
                <Button
                  label="Download roster"
                  variant="secondary"
                  size="small"
                  icon={Download}
                  onClick={handleDownloadTemplate}
                />
                <Button
                  label="Upload Excel"
                  variant="secondary"
                  size="small"
                  icon={UploadIcon}
                  onClick={handleUploadExcel}
                />
              </div>
            ) : null}
            <Button
              label="Add Person"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={handleOpenModal}
            />
          </div>
        </div>

        {/* Table/List */}
        <div className="dpr-personnel-table-shell">
          {isOffshore ? (
            <div className="dpr-personnel-offshore-table">
              <div className="dpr-personnel-offshore-header dpr-personnel-offshore-grid">
                {[
                  { label: 'ID' },
                  { label: 'JOB DESCRIPTION' },
                  { label: 'SUB JOB' },
                  { label: 'LAST NAME' },
                  { label: 'FIRST NAME' },
                  { label: 'NATIONALITY' },
                  { label: 'ONBOARD', center: true },
                  { label: 'SHIFT', center: true },
                  { label: 'STATION', center: true },
                  { label: 'WELDER ID', center: true },
                  { label: 'ACTION', center: true },
                ].map((col) => (
                  <div
                    key={col.label}
                    className={`dpr-personnel-offshore-header__cell${col.center ? ' dpr-personnel-offshore-header__cell--center' : ''}`}
                  >
                    {col.label}
                  </div>
                ))}
              </div>
              <div className="dpr-personnel-offshore-rows">
                {personnelRows.length === 0 ? (
                  <div className="dpr-personnel-offshore-empty">
                    No personnel added. Download the roster template or upload an Excel file to get started.
                  </div>
                ) : (
                  personnelRows.map((row) => (
                    <div
                      key={row.id}
                      onMouseEnter={() => setHoveredRow(row.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className={`dpr-personnel-offshore-row dpr-personnel-offshore-grid${hoveredRow === row.id ? ' dpr-personnel-offshore-row--hover' : ''}`}
                    >
                      <input
                        type="text"
                        value={row.rosterId ?? ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'rosterId', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <input
                        type="text"
                        value={row.jobDescription || ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'jobDescription', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                        placeholder="Job description"
                      />
                      <input
                        type="text"
                        value={row.subJob || ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'subJob', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                        placeholder="Sub job"
                      />
                      <input
                        type="text"
                        value={row.lastName || ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'lastName', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <input
                        type="text"
                        value={row.firstName || ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'firstName', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <input
                        type="text"
                        value={row.nationality || ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'nationality', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <DprPipelineSelect
                        value={row.onboard === true ? 'yes' : row.onboard === false ? 'no' : ''}
                        options={OFFSHORE_ONBOARD_OPTIONS}
                        onChange={(val) => handleOffshoreRowChange(row.id, 'onboardSelect', val ?? '')}
                        placeholder="—"
                        appearance="cell"
                        searchable={false}
                        className="dpr-personnel-offshore-select"
                        aria-label="Onboard"
                      />
                      <DprPipelineSelect
                        value={row.rosterShift || getDefaultOffshoreRosterShift(shift)}
                        options={getOffshoreShiftSelectOptions(row.rosterShift, shift)}
                        onChange={(val) => handleOffshoreRowChange(row.id, 'rosterShift', val ?? '')}
                        placeholder="—"
                        appearance="cell"
                        searchable={false}
                        className="dpr-personnel-offshore-select"
                        aria-label="Shift"
                      />
                      <input
                        type="text"
                        placeholder="Station"
                        value={row.allocatedStation != null ? String(row.allocatedStation) : ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'allocatedStation', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <input
                        type="text"
                        placeholder="Welder ID"
                        value={row.welderId != null ? String(row.welderId) : ''}
                        onChange={(e) => handleOffshoreRowChange(row.id, 'welderId', e.target.value)}
                        className="dpr-personnel-offshore-cell-input"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteRow(row.id)}
                        className="dpr-personnel-offshore-delete"
                        aria-label="Delete person"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <Header
                  items={[
                    { label: 'NAME', width: '10%' },
                    { align: 'center', label: 'ROLE / DESIGNATION', width: 'flex: 1' },
                    { align: 'center', label: 'STATION', width: '12%' },
                    { align: 'center', label: 'PLANNED HOURS', width: '12%' },
                    { align: 'center', label: 'ACTUAL HOURS', width: '12%' },
                    { align: 'center', label: 'ACTION', width: '10%' },
                  ]}
                  size="small"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                {personnelRows.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>
                    No personnel added.
                  </div>
                ) : (
                  personnelRows.map((row) => (
                    <div
                      key={row.id}
                      onMouseEnter={() => setHoveredRow(row.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        border: hoveredRow === row.id ? '1px solid #00C9A7' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        transition: 'border-color 0.2s ease'
                      }}>
                      <div style={{ width: '30%' }}>
                        <CustomNameSelect
                          value={row.name || ''}
                          options={allPersonnelEntries}
                          onChange={(val) => handleRowChange(row.id, 'name', val)}
                          placeholder="Select person..."
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <CustomRoleSelect
                          value={row.role || ''}
                          options={filteredAvailableRoles}
                          onChange={(val) => handleRowChange(row.id, 'role', val)}
                        />
                      </div>
                      <div style={{ width: '12%' }}>
                        {isWelderRoleName(row.role) ? (
                          <input
                            type="text"
                            placeholder="Station #"
                            value={row.allocatedStation != null ? String(row.allocatedStation) : ''}
                            onChange={(e) => handleRowChange(row.id, 'allocatedStation', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              border: '1px solid #E5E7EB',
                              background: '#fff',
                              color: '#1F2937',
                              fontSize: '13px',
                              boxSizing: 'border-box',
                            }}
                          />
                        ) : (
                          <div style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center' }}>—</div>
                        )}
                      </div>
                      <div style={{ width: '12%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          backgroundColor: '#FFFFFF'
                        }}>
                          <input
                            type="number"
                            value={row.planned || 0}
                            onChange={(e) => handleRowChange(row.id, 'planned', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '40px',
                              padding: '0',
                              fontSize: '14px',
                              fontWeight: 600,
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: '#1F2937',
                              textAlign: 'center',
                              outline: 'none'
                            }}
                          />
                          <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 400, marginLeft: '4px' }}>Hrs</span>
                        </div>
                      </div>
                      <div style={{ width: '12%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          backgroundColor: '#FFFFFF'
                        }}>
                          <input
                            type="number"
                            value={row.actual || 0}
                            onChange={(e) => handleRowChange(row.id, 'actual', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '40px',
                              padding: '0',
                              fontSize: '14px',
                              fontWeight: 600,
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: '#1F2937',
                              textAlign: 'center',
                              outline: 'none'
                            }}
                          />
                          <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 400, marginLeft: '4px' }}>Hrs</span>
                        </div>
                      </div>
                      <div style={{ width: '10%', display: 'flex', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleDeleteRow(row.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#D1D5DB',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#D1D5DB'}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <CommentBox
          header="Personnel Notes / Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter any additional personnel remarks..."
        />

        {/* Save Button - Sticky */}
        <div style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '24px',
          zIndex: 10
        }}>
          <Button
            label={
              typeof isLastDprSection === 'boolean'
                ? (isLastDprSection ? 'Save section' : 'Save & Next')
                : 'Save Personnel Report'
            }
            variant="secondary"
            icon={Save}
            onClick={handleSave}
            disabled={isSaving || isSubmitting}
          />
        </div>

      </div >

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title="Delete Personnel"
        message="Are you sure you want to delete this person? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#FF3B30', // Red color matching reference visual (approx) or use #EF4444
            borderColor: '#FF3B30'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Personnel"
        message={`Are you sure you want to skip the Personnel log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipPersonnel}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />



      {/* Add New Person Modal - Keeping existing modal logic but ensuring it matches basic style or is hidden properly */}
      {
        isModalVisible && (
          <div className="dpr-personnel-add-modal" style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '100px',
            zIndex: 1000
          }}>
            <div className={`dpr-personnel-add-modal__panel${isOffshore ? '' : ' dpr-personnel-add-modal__panel--onshore'}`}>
              {/* Header */}
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{
                    backgroundColor: '#EEF2FF',
                    padding: '10px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4F46E5'
                  }}>
                    <UserPlus size={24} />
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: '#111827', textAlign: 'left' }}>Add New Person</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6B7280', textAlign: 'left' }}>Enter details for the new team member.</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9CA3AF',
                    padding: '4px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                {isOffshore ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          ID
                        </label>
                        <input
                          type="text"
                          value={newPersonForm.rosterId}
                          onChange={(e) => handleFormChange('rosterId', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Shift
                        </label>
                        <DprPipelineSelect
                          value={newPersonForm.rosterShift || getDefaultOffshoreRosterShift(shift)}
                          options={getOffshoreShiftSelectOptions(newPersonForm.rosterShift, shift)}
                          onChange={(val) => handleFormChange('rosterShift', val ?? '')}
                          placeholder="Select shift"
                          appearance="field"
                          searchable={false}
                          className="dpr-personnel-form-select-wrap"
                          aria-label="Shift"
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                        Job Description <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Senior Technician"
                        value={newPersonForm.jobDescription}
                        onChange={(e) => handleFormChange('jobDescription', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                        Sub Job
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Lead"
                        value={newPersonForm.subJob}
                        onChange={(e) => handleFormChange('subJob', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Last Name <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={newPersonForm.lastName}
                          onChange={(e) => handleFormChange('lastName', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          First Name <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={newPersonForm.firstName}
                          onChange={(e) => handleFormChange('firstName', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Nationality
                        </label>
                        <input
                          type="text"
                          value={newPersonForm.nationality}
                          onChange={(e) => handleFormChange('nationality', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Onboard
                        </label>
                        <DprPipelineSelect
                          value={newPersonForm.onboard || 'yes'}
                          options={OFFSHORE_ONBOARD_OPTIONS}
                          onChange={(val) => handleFormChange('onboard', val ?? 'yes')}
                          placeholder="Select onboard"
                          appearance="field"
                          searchable={false}
                          className="dpr-personnel-form-select-wrap"
                          aria-label="Onboard"
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Welder ID
                          {isWelderJobDescription(newPersonForm.jobDescription) ? (
                            <span style={{ color: '#EF4444' }}> *</span>
                          ) : null}
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. W001"
                          value={newPersonForm.welderId}
                          onChange={(e) => handleFormChange('welderId', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151', textAlign: 'left' }}>
                          Station
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. st 1"
                          value={newPersonForm.allocatedStation || ''}
                          onChange={(e) => handleFormChange('allocatedStation', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Full Name <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={newPersonForm.fullName}
                    onChange={(e) => handleFormChange('fullName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Role <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <CustomRoleSelect
                    value={newPersonForm.role || ''}
                    options={filteredAvailableRoles}
                    placeholder="Select Role"
                    onChange={(val) => handleFormChange('role', val)}
                  />
                </div>
                {newPersonForm.role && isWelderRoleName(newPersonForm.role) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Welder ID <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. W001"
                      value={newPersonForm.welderId}
                      onChange={(e) => handleFormChange('welderId', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    />
                  </div>
                )}

                {newPersonForm.role && isWelderRoleName(newPersonForm.role) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Station number
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 1"
                      value={newPersonForm.allocatedStation || ''}
                      onChange={(e) => handleFormChange('allocatedStation', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box',
                        backgroundColor: '#FFFFFF'
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                    />
                  </div>
                )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px',
                backgroundColor: '#F9FAFB',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <Button
                  label="Cancel"
                  variant="tertiary"
                  onClick={handleCloseModal}
                  size="medium"
                />
                <Button
                  label="Add Person"
                  variant="primary"
                  onClick={handleAddPerson}
                  disabled={
                    isOffshore
                      ? !newPersonForm.firstName?.trim() || !newPersonForm.lastName?.trim() || !newPersonForm.jobDescription?.trim()
                      : !newPersonForm.fullName || !newPersonForm.role
                  }
                  size="medium"
                />
              </div>
            </div>
          </div>
        )
      }

      <Modal
        title="Upload Personnel Roster (Excel)"
        open={isUploadModalVisible}
        onCancel={handleUploadModalCancel}
        onOk={handleUploadExcelFile}
        okText="Parse file"
        destroyOnClose
        className="dpr-personnel-excel-modal"
        wrapClassName="dpr-personnel-excel-modal-wrap"
      >
        <Upload.Dragger {...uploadProps} className="dpr-personnel-excel-upload">
          <p className="ant-upload-text">Click or drag Excel file to this area</p>
          <p className="ant-upload-hint">Only .xls or .xlsx files are supported.</p>
        </Upload.Dragger>
      </Modal>

      <Modal
        title="Confirm roster import"
        open={isExcelPreviewOpen}
        onCancel={() => {
          setIsExcelPreviewOpen(false);
          setExcelPreviewRows([]);
        }}
        onOk={handleConfirmExcelImport}
        okText="Replace all"
        width={900}
        destroyOnClose
        className="dpr-personnel-excel-modal"
        wrapClassName="dpr-personnel-excel-modal-wrap"
      >
        <p style={{ marginBottom: '16px', color: '#64748B' }}>
          {excelPreviewRows.length} row(s) will replace the current table. Click Save after confirming to persist.
        </p>
        <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['ID', 'Job Description', 'Sub Job', 'Last Name', 'First Name', 'Nationality', 'Onboard', 'Shift', 'Station'].map((h) => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {excelPreviewRows.map((row, idx) => (
                <tr key={`preview-${idx}`}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.rosterId}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.jobDescription}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.subJob}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.lastName}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.firstName}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.nationality}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.onboard === true ? 'Yes' : row.onboard === false ? 'No' : ''}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.rosterShift}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #F1F5F9' }}>{row.allocatedStation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div >
  );
};

export default PersonnelLog;

